import flatMap from 'lodash/flatMap.js';
import get from 'lodash/get.js';
import unionBy from 'lodash/unionBy.js';

import User from '@/models/User.js';
import axios from '@/plugins/axios.js';
import Gate from '@/plugins/vue-gate/Gate.js';
import Utilities from '@/utilities/Utilities.js';

export default {
    state: {
        user: null,
        sessionTimedOut: false,
        sessionTimeoutModalVisible: false,
        returnPath: null,
    },

    getters: {
        user: state => state.user,
        userId: state => get(state.user, 'id'),
        check: state => state.user !== null,
        isSessionTimedOut: state => state.sessionTimedOut,
        isSessionTimeoutModalVisible: state => state.sessionTimeoutModalVisible,
        getReturnPath: state => state.returnPath,
    },

    actions: {
        saveToken({ commit, dispatch }, { token, $echo }) {
            return new Promise(resolve => {
                axios.defaults.headers.common.Authorization = `Bearer ${token}`;

                commit('SAVE_TOKEN', token);

                dispatch('Online/createWebsocketConnection', $echo, {
                    root: true,
                });

                resolve();
            });
        },

        async fetchUser({ commit, dispatch }, force = false) {
            try {
                let user = Utilities.initialState('AuthenticatedUser');

                if (force) {
                    try {
                        const { data } = await axios.get('/api/user', { errorHandle: false });
                        user = data.data;
                    } catch (_e) {
                        return null;
                    }
                }

                if (!user) {
                    return null;
                }

                await dispatch('hydrateUser', user);

                // Reset session timeout state when user is successfully fetched
                commit('SET_SESSION_TIMED_OUT', false);

                return user;
            } catch (e) {
                console.error(e);
                commit('FETCH_USER_FAILURE');
            }
        },

        async fetchUserWithState({ commit, dispatch }, force = false) {
            try {
                let user = Utilities.initialState('AuthenticatedUser');

                let state;

                if (!get(user, 'id') || force) {
                    try {
                        const { data } = await axios.get('/api/user?append=state', { errorHandle: false });
                        user = data.data;

                        state = data.state;
                    } catch (_e) {
                        return null;
                    }
                }

                await dispatch('hydrateUser', user);

                // Reset session timeout state when user is successfully fetched
                commit('SET_SESSION_TIMED_OUT', false);

                return { user, state };
            } catch (e) {
                console.error(e);
                commit('FETCH_USER_FAILURE');
            }
        },

        async hydrateUser({ commit, dispatch }, user) {
            await User.insert({ data: user });

            const userModel = User.query().with(Gate.userRelationships()).find(get(user, 'id'));

            // Dispatch below is used to update the User store module, this store module has getters
            // that are used throughout Edverum to check for different properties
            // TODO: Refactor to use vuex-orm User model
            // Until then this is necessary for areas in edverum that rely on the User store module.

            dispatch('User/hydrate', userModel, { root: true });

            commit('UPDATE_USER', userModel);

            commit('ASSIGN_ROLES_AND_PERMISSIONS', { user });
        },

        updateUser({ commit }, user) {
            commit('UPDATE_USER', user);
        },

        /**
         * Properly logout the user from the client side
         * @param commit
         * @return {Promise<void>}
         */
        async logout({ commit }) {
            commit('LOGOUT');
            delete axios.defaults.headers.common.Authorization;
        },

        /**
         * Only use once 401 received from backend, implemented to prevent 401 loops.
         * @param commit
         * @return {Promise<void>}
         */
        clear({ commit }) {
            commit('LOGOUT');
        },

        /**
         * Set the session timeout state
         * @param commit
         * @param {boolean} timedOut Whether the session has timed out
         */
        setSessionTimedOut({ commit, state }, timedOut) {
            if (state.sessionTimedOut !== timedOut) {
                commit('SET_SESSION_TIMED_OUT', timedOut);

                if (timedOut) {
                } else {
                }
            }
        },

        /**
         * Show or hide the session timeout modal
         * @param commit
         * @param {boolean} visible Whether the modal should be visible
         */
        showSessionTimeoutModal({ commit }, visible) {
            commit('SET_SESSION_TIMEOUT_MODAL_VISIBLE', visible);
        },

        /**
         * Save the current path to return to after re-authentication
         * @param commit
         * @param {string} path Path to return to
         */
        saveReturnPath({ commit }, path) {
            commit('SET_RETURN_PATH', path);
        },
    },

    mutations: {
        ASSIGN_ROLES_AND_PERMISSIONS(state, { user }) {
            state.roles = user.roles;

            state.permissions = unionBy([...flatMap(user.roles, role => role.permissions), ...user.permissions], 'id');
        },

        UPDATE_USER(state, user) {
            state.user = user;
        },

        UPDATE_QUIZ_ACTIVE_ID(state, quizId) {
            if (!state.user) {
                return;
            }

            state.user.quiz_active_id = quizId;
        },

        LOGOUT(state) {
            state.user = null;
            state.roles = [];
            state.permissions = [];
            // Don't reset session timeout flags on logout as they may be needed for the login flow
        },

        SET_SESSION_TIMED_OUT(state, value) {
            state.sessionTimedOut = value;

            if (value === true && typeof window !== 'undefined' && window.SessionMonitor) {
                window.SessionMonitor.handleSessionTimeout();
            }
        },

        SET_SESSION_TIMEOUT_MODAL_VISIBLE(state, visible) {
            state.sessionTimeoutModalVisible = visible;
        },

        SET_RETURN_PATH(state, path) {
            state.returnPath = path;
        },
    },
};
